import { useState } from "react";
import { motion } from "framer-motion";
import { siteConfig, contactData } from "../data/mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Send } from "lucide-react";
import axios from "axios";

// This logic now handles the "undefined" error gracefully[cite: 1]
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    
    try {
      // We added a log here so you can see the exact URL in your browser console[cite: 1]
      console.log("Attempting to send to:", `${API}/contact`);
      
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. Thank you.");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      // This now logs the REAL error to your console so we can find it[cite: 1]
      console.error("Contact Form Error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white min-h-screen pt-32 pb-24 md:pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20 md:mb-28"
        >
          <h1 className="font-serif text-[32px] md:text-[42px] text-[#1a1a1a] tracking-[-0.02em]">
            Contact
          </h1>
          <p className="font-serif text-[17px] md:text-[20px] text-[#666] mt-6 leading-[1.7] max-w-[500px] italic">
            {contactData.closingLine}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label className="text-[11px] tracking-[0.15em] uppercase text-[#999] block mb-3">
                  Name
                </label>
                <Input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="contact-input border-0 border-b border-[#e0ded9] rounded-none bg-transparent px-0 py-3 text-[15px] text-[#1a1a1a] placeholder:text-[#ccc] focus-visible:ring-0 focus-visible:border-[#1a1a1a] transition-colors duration-500"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.15em] uppercase text-[#999] block mb-3">
                  Email
                </label>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="contact-input border-0 border-b border-[#e0ded9] rounded-none bg-transparent px-0 py-3 text-[15px] text-[#1a1a1a] placeholder:text-[#ccc] focus-visible:ring-0 focus-visible:border-[#1a1a1a] transition-colors duration-500"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.15em] uppercase text-[#999] block mb-3">
                  Message
                </label>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project..."
                  rows={5}
                  className="contact-input border-0 border-b border-[#e0ded9] rounded-none bg-transparent px-0 py-3 text-[15px] text-[#1a1a1a] placeholder:text-[#ccc] focus-visible:ring-0 focus-visible:border-[#1a1a1a] transition-colors duration-500 resize-none"
                />
              </div>

              <motion.button
                type="submit"
                disabled={sending}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-3 mt-4 text-[12px] tracking-[0.15em] uppercase text-[#1a1a1a] bg-transparent border border-[#1a1a1a] px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors duration-500 disabled:opacity-40"
              >
                {sending ? "Sending..." : sent ? "Sent" : "Send Message"}
                <Send size={13} className="transition-transform duration-500 group-hover:translate-x-0.5" />
              </motion.button>
            </form>
          </motion.div>

          {/* Direct Contact Links - Uses your existing siteConfig[cite: 1] */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-5 lg:pt-4"
          >
            <div className="space-y-10">
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-3">Email</p>
                <a href={`mailto:${siteConfig.email}`} className="text-[15px] text-[#1a1a1a] hover:opacity-60 transition-opacity duration-500">
                  {siteConfig.email}
                </a>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-3">Vimeo</p>
                <a href={siteConfig.vimeo} target="_blank" rel="noopener noreferrer" className="text-[15px] text-[#1a1a1a] hover:opacity-60 transition-opacity duration-500">
                  Vimeo Profile
                </a>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-3">YouTube</p>
                <a href={siteConfig.youtube} target="_blank" rel="noopener noreferrer" className="text-[15px] text-[#1a1a1a] hover:opacity-60 transition-opacity duration-500">
                  YouTube Channel
                </a>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-3">Letterboxd</p>
                <a href={siteConfig.letterboxd} target="_blank" rel="noopener noreferrer" className="text-[15px] text-[#1a1a1a] hover:opacity-60 transition-opacity duration-500">
                  Sumo Didi on Letterboxd
                </a>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#bbb] mb-3">IMDb</p>
                <a href={siteConfig.imdb} target="_blank" rel="noopener noreferrer" className="text-[15px] text-[#1a1a1a] hover:opacity-60 transition-opacity duration-500">
                  IMDb Profile
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
